import React, { useEffect, useRef, useState } from "react";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiMapPin,
  FiNavigation,
  FiShoppingCart,
  FiUserCheck,
} from "react-icons/fi";

import markAttendance from "../../assets/mobileScreenshots/markAttendance.jpeg";
import unscheduledVisitInProgress from "../../assets/mobileScreenshots/visitInProgress.jpeg";
import checkOutGps from "../../assets/mobileScreenshots/checkoutWithOrder.jpeg";
import checkoutWithOrder from "../../assets/mobileScreenshots/checkoutWithSalesOrder.jpeg";
import checkOutNoOrder from "../../assets/mobileScreenshots/checkoutNoOrder.jpeg";
import unscheduledVisitCompleted from "../../assets/mobileScreenshots/successfulVisit.jpeg";
import endAttendance from "../../assets/mobileScreenshots/endAttendance.jpeg";

import "./sfaMobileExperience.css";
import SfaBookingModal from "./sfaBookingModal";

const experiences = [
  {
    id: "01",
    title: "Start With Attendance",
    shortTitle: "Attendance",
    description:
      "Sales representatives can begin their working day by recording attendance directly from the mobile application. The attendance workflow provides a clear starting point for field execution, while check-in details and the day's attendance status remain available for reference throughout the working day.",
    icon: <FiUserCheck />,
    accent: "teal",
    screen: markAttendance,
    label: "Today's Status",
    detail: "Attendance & daily field context",
  },
  {
    id: "02",
    title: "Execute Planned & Unscheduled Visits",
    shortTitle: "Visit Execution",
    description:
      "Sales teams can work through their planned outlet visits while also handling unscheduled opportunities that arise during the day. The mobile workflow keeps outlet activity, visit status and customer information together so representatives can manage both planned coverage and additional market visits from the field.",
    icon: <FiMapPin />,
    accent: "orange",
    screen: unscheduledVisitInProgress,
    label: "Unscheduled Visits",
    detail: "Planned and additional field activity",
  },
  {
    id: "03",
    title: "Capture Location-Aware Activity",
    shortTitle: "GPS Activity",
    description:
      "Field visits can be performed with location context captured as part of the visit workflow. Sales representatives can complete check-in and check-out activity while GPS information provides additional context around where the field activity was performed and when the visit took place.",
    icon: <FiNavigation />,
    accent: "cyan",
    screen: checkOutGps,
    label: "GPS Ready",
    detail: "Location-aware visit execution",
  },
  {
    id: "04",
    title: "Turn Visits Into Orders",
    shortTitle: "Order Capture",
    description:
      "When a customer interaction results in a sale, representatives can move directly from the field visit into order-related activity. This keeps productive customer interactions connected with sales execution and helps ensure that order information is captured as part of the field-sales workflow instead of being handled separately.",
    icon: <FiShoppingCart />,
    accent: "purple",
    screen: checkoutWithOrder,
    label: "With Order",
    detail: "Productive visit & order activity",
  },
  {
    id: "05",
    title: "Record the Visit Outcome",
    shortTitle: "Visit Outcome",
    description:
      "Not every outlet visit results in an order, and the application provides a structured way to record what happened during the interaction. Representatives can capture a no-order outcome along with the relevant reason, remarks and feedback, creating a more complete record of customer engagement.",
    icon: <FiFileText />,
    accent: "coral",
    screen: checkOutNoOrder,
    label: "No Order",
    detail: "Outcome, reason & feedback capture",
  },
  {
    id: "06",
    title: "Complete the Field Visit",
    shortTitle: "Visit Complete",
    description:
      "Once the customer interaction is finished, representatives can complete the visit and maintain the corresponding check-in and check-out information. This creates a clear record of completed field activity and gives managers better visibility into which customer interactions have actually been executed.",
    icon: <FiCheckCircle />,
    accent: "green",
    screen: unscheduledVisitCompleted,
    label: "Visited",
    detail: "Completed field activity",
  },
  {
    id: "07",
    title: "Close the Day With Complete Visibility",
    shortTitle: "Day End",
    description:
      "At the end of the working day, representatives can review a consolidated summary of their field execution, including attendance, completed visits, working hours and field distance. This provides a clear day-end view of activity and helps connect the work completed in the market with the overall sales execution record.",
    icon: <FiCheckCircle />,
    accent: "teal",
    screen: endAttendance,
    label: "Day Completed",
    detail: "Daily attendance & activity summary",
  },
];

const SfaMobileExperience = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSfaDemo, setShowSfaDemo] = useState(false);
  const journeyRef = useRef(null);
  const stepRefs = useRef([]);

  const activeExperience = experiences[activeIndex];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((previous) =>
        previous === experiences.length - 1 ? 0 : previous + 1,
      );
    }, 7000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const activeStep = stepRefs.current[activeIndex];
    const journey = journeyRef.current;

    if (!activeStep || !journey || window.innerWidth > 767) {
      return;
    }

    const journeyRect = journey.getBoundingClientRect();
    const stepRect = activeStep.getBoundingClientRect();

    if (
      stepRect.left < journeyRect.left ||
      stepRect.right > journeyRect.right
    ) {
      journey.scrollTo({
        left:
          activeStep.offsetLeft -
          journey.clientWidth / 2 +
          activeStep.offsetWidth / 2,
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  return (
    <>
      <section
        id="sfa-mobile-experience"
        className="sfa-mobile-experience-section"
      >
        <div className="sfa-mobile-experience-bg"></div>

        <div
          className="sfa-mobile-experience-glow sfa-mobile-glow-one"
          aria-hidden="true"
        ></div>

        <div
          className="sfa-mobile-experience-glow sfa-mobile-glow-two"
          aria-hidden="true"
        ></div>

        <div className="container">
          <div className="sfa-mobile-heading">
            <div className="sfa-mobile-eyebrow">
              <span></span>
              SFA MOBILE EXPERIENCE
            </div>

            <h2>
              Everything Your Sales Team Needs,
              <span>Right in Their Hands.</span>
            </h2>

            <p>
              From starting the working day with attendance to executing outlet
              visits, capturing orders and completing field activity,
              TechCognics SFA brings everyday sales execution into one connected
              mobile experience.
            </p>
          </div>

          <div className="sfa-mobile-product">
            <div className="sfa-mobile-journey" ref={journeyRef}>
              <div className="sfa-mobile-journey-line"></div>

              {experiences.map((experience, index) => (
                <button
                  type="button"
                  key={experience.id}
                  ref={(element) => {
                    stepRefs.current[index] = element;
                  }}
                  className={`sfa-mobile-step ${
                    index === activeIndex ? "sfa-mobile-step-active" : ""
                  } ${index < activeIndex ? "sfa-mobile-step-completed" : ""}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show ${experience.shortTitle}`}
                >
                  <span className="sfa-mobile-step-node">
                    {index < activeIndex ? <FiCheckCircle /> : experience.icon}
                  </span>

                  <span className="sfa-mobile-step-copy">
                    <small>{experience.id}</small>
                    <strong>{experience.shortTitle}</strong>
                  </span>
                </button>
              ))}
            </div>

            <div className="sfa-mobile-showcase">
              <div className="sfa-mobile-copy">
                <div className="sfa-mobile-copy-index">
                  <span>{activeExperience.id}</span>

                  <div>
                    <span>FIELD SALES WORKFLOW</span>
                    <strong>{activeExperience.label}</strong>
                  </div>
                </div>

                <h3 key={activeExperience.id}>{activeExperience.title}</h3>

                <p key={`${activeExperience.id}-description`}>
                  {activeExperience.description}
                </p>

                <div
                  key={`${activeExperience.id}-detail`}
                  className={`sfa-mobile-detail sfa-mobile-detail-${activeExperience.accent}`}
                >
                  <span className="sfa-mobile-detail-icon">
                    {activeExperience.icon}
                  </span>

                  <div>
                    <strong>{activeExperience.detail}</strong>
                    <small>Connected to the sales execution workflow</small>
                  </div>
                </div>

                <div className="sfa-mobile-progress">
                  <div className="sfa-mobile-progress-track">
                    <span
                      style={{
                        width: `${
                          ((activeIndex + 1) / experiences.length) * 100
                        }%`,
                      }}
                    ></span>
                  </div>

                  <div className="sfa-mobile-progress-text">
                    <span>
                      {String(activeIndex + 1).padStart(2, "0")} /{" "}
                      {String(experiences.length).padStart(2, "0")}
                    </span>

                    <span>Auto walkthrough</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="sfa-mobile-demo-btn"
                  onClick={() => setShowSfaDemo(true)}
                >
                  See SFA in Action
                  <span>
                    <FiArrowRight />
                  </span>
                </button>
              </div>

              <div className="sfa-mobile-device-area">
                <div className="sfa-mobile-device-glow"></div>

                <div className="sfa-mobile-device">
                  <div className="sfa-mobile-device-camera"></div>

                  <div className="sfa-mobile-device-screen">
                    <img
                      key={activeExperience.id}
                      src={activeExperience.screen}
                      alt={`TechCognics SFA ${activeExperience.label} screen`}
                    />
                  </div>
                </div>

                <div className="sfa-mobile-live-status">
                  <span></span>
                  FIELD ACTIVITY
                </div>

                <div className="sfa-mobile-screen-card">
                  <div className="sfa-mobile-screen-mini-icon">
                    {activeExperience.icon}
                  </div>

                  <div>
                    <small>CURRENT STEP</small>
                    <strong>{activeExperience.shortTitle}</strong>
                  </div>
                </div>

                <div className="sfa-mobile-date-pill">
                  <FiClock />
                  <span>
                    {activeIndex === 6 ? "DAY COMPLETE" : "LIVE EXECUTION"}
                  </span>
                </div>
              </div>
            </div>

            <div className="sfa-mobile-bottom">
              <div className="sfa-mobile-bottom-item">
                <span className="sfa-mobile-bottom-icon sfa-bottom-teal">
                  <FiUserCheck />
                </span>

                <div>
                  <strong>Attendance</strong>
                  <span>Start the field day</span>
                </div>
              </div>

              <div className="sfa-mobile-bottom-item">
                <span className="sfa-mobile-bottom-icon sfa-bottom-orange">
                  <FiNavigation />
                </span>

                <div>
                  <strong>Visit Execution</strong>
                  <span>Work the daily plan</span>
                </div>
              </div>

              <div className="sfa-mobile-bottom-item">
                <span className="sfa-mobile-bottom-icon sfa-bottom-purple">
                  <FiShoppingCart />
                </span>

                <div>
                  <strong>Order Capture</strong>
                  <span>Convert visits into orders</span>
                </div>
              </div>

              <div className="sfa-mobile-bottom-item">
                <span className="sfa-mobile-bottom-icon sfa-bottom-green">
                  <FiCheckCircle />
                </span>

                <div>
                  <strong>Day End</strong>
                  <span>Review field activity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SfaBookingModal
        show={showSfaDemo}
        onClose={() => setShowSfaDemo(false)}
      />
    </>
  );
};

export default SfaMobileExperience;
